import React, { useState, useEffect } from "react";
import AppLayout from "../components/AppLayout";
import { api } from "../lib/api";
import { toast } from "sonner";
import PricingPlatformManagement from "./PricingPlatformManagement";
import {
  DollarSign, TrendingUp, Sparkles, RefreshCw, Save, Check,
  History, BarChart3, Package, Tag, Calendar, AlertCircle,
  ChevronDown, ChevronUp, Zap, Target, Crown
} from "lucide-react";

const PLATFORMS = [
  { key: "onlyfans_vip", label: "OnlyFans VIP", color: "#A855F7", field: "onlyfans_vip_presale" },
  { key: "onlyfans_free", label: "OnlyFans Free", color: "#22C55E", field: "onlyfans_free_ppv" },
  { key: "justforfans", label: "JustForFans", color: "#F59E0B", field: "justforfans_subscriber" },
];

export default function PricingStrategy() {
  const [tab, setTab] = useState("catalog"); // catalog | history | recommendations | subscriptions
  const [platformTab, setPlatformTab] = useState("onlyfans_vip");
  const [videos, setVideos] = useState([]);
  const [history, setHistory] = useState([]);
  const [recommendations, setRecommendations] = useState(null);
  const [selectedVideos, setSelectedVideos] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [aiLoading, setAiLoading] = useState(false);
  const [savingPrices, setSavingPrices] = useState(false);
  const [bulkPrice, setBulkPrice] = useState("");
  const [promoTag, setPromoTag] = useState("");
  const [metrics, setMetrics] = useState(null);
  const [jffReportedPerformance, setJffReportedPerformance] = useState({ promo_codes: [], cancellation_reasons: [] });
  
  // Subscription pricing state
  const [subscriptionPricing, setSubscriptionPricing] = useState({});
  const [subscriptionForm, setSubscriptionForm] = useState({
    platform: "onlyfans_vip",
    duration_months: 1,
    regular_price: "",
    promo_price: "",
    promo_description: "",
    promo_active: false,
    promo_start_date: "",
    promo_end_date: "",
  });
  const [savingSubscription, setSavingSubscription] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [catalogRes, historyRes, metricsRes, subscriptionRes, jffPerformanceRes] = await Promise.all([
        api.get("/api/pricing-strategy/catalog-videos"),
        api.get("/api/pricing-strategy/pricing-history?limit=200"),
        api.get("/api/pricing-strategy/performance-metrics"),
        api.get("/api/pricing-strategy/subscriptions"),
        api.get("/api/pricing-strategy/justforfans/reported-performance")
          .catch(() => ({ data: { promo_codes: [], cancellation_reasons: [] } })),
      ]);
      setVideos(catalogRes.data.videos || []);
      setHistory(historyRes.data.history || []);
      setMetrics(metricsRes.data.tiers || {});
      setSubscriptionPricing(subscriptionRes.data.tiers || {});
      setJffReportedPerformance(jffPerformanceRes.data || { promo_codes: [], cancellation_reasons: [] });
    } catch (e) {
      toast.error("Failed to load pricing data");
    } finally {
      setLoading(false);
    }
  };

  const getAIRecommendations = async () => {
    setAiLoading(true);
    try {
      const res = await api.post("/api/pricing-strategy/ai-recommendations");
      setRecommendations(res.data.data);
      toast.success("AI recommendations generated!");
      setTab("recommendations");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to generate recommendations");
    } finally {
      setAiLoading(false);
    }
  };

  const handlePriceChange = (videoId, field, value) => {
    setVideos(prev => prev.map(v => {
      if (v.id === videoId) {
        return {
          ...v,
          pricing: {
            ...v.pricing,
            [field]: value === "" ? null : parseFloat(value)
          }
        };
      }
      return v;
    }));
  };

  const toggleVideoSelection = (videoId) => {
    setSelectedVideos(prev => {
      const newSet = new Set(prev);
      if (newSet.has(videoId)) {
        newSet.delete(videoId);
      } else {
        newSet.add(videoId);
      }
      return newSet;
    });
  };

  const selectAll = () => {
    if (selectedVideos.size === videos.length) {
      setSelectedVideos(new Set());
    } else {
      setSelectedVideos(new Set(videos.map(v => v.id)));
    }
  };

  const applyBulkPrice = () => {
    if (!bulkPrice || selectedVideos.size === 0) {
      toast.error("Select videos and enter a price");
      return;
    }

    const price = parseFloat(bulkPrice);
    const platform = PLATFORMS.find(p => p.key === platformTab);
    if (!platform) return;

    setVideos(prev => prev.map(v => {
      if (selectedVideos.has(v.id)) {
        return {
          ...v,
          pricing: {
            ...v.pricing,
            [platform.field]: price
          }
        };
      }
      return v;
    }));

    toast.success(`Applied $${price} to ${selectedVideos.size} video(s)`);
    setBulkPrice("");
  };

  const savePrices = async () => {
    if (selectedVideos.size === 0) {
      toast.error("Select videos to save prices");
      return;
    }

    setSavingPrices(true);
    try {
      const videoIds = Array.from(selectedVideos);
      const sampleVideo = videos.find(v => v.id === videoIds[0]);
      
      await api.post("/api/pricing-strategy/set-prices", {
        video_ids: videoIds,
        pricing: sampleVideo.pricing,
        promo_tag: promoTag || null,
        notes: null,
      });

      toast.success(`Prices saved for ${videoIds.length} video(s)!`);
      setSelectedVideos(new Set());
      setPromoTag("");
      await loadData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save prices");
    } finally {
      setSavingPrices(false);
    }
  };

  const saveSubscriptionPricing = async () => {
    if (!subscriptionForm.regular_price) {
      toast.error("Regular price is required");
      return;
    }

    setSavingSubscription(true);
    try {
      await api.post("/api/pricing-strategy/subscriptions", {
        tier_name: subscriptionForm.platform,
        duration_months: parseInt(subscriptionForm.duration_months),
        regular_price: parseFloat(subscriptionForm.regular_price),
        promo_price: subscriptionForm.promo_price ? parseFloat(subscriptionForm.promo_price) : null,
        promo_description: subscriptionForm.promo_description || null,
        promo_active: subscriptionForm.promo_active,
        promo_start_date: subscriptionForm.promo_start_date || null,
        promo_end_date: subscriptionForm.promo_end_date || null,
      });

      toast.success(`Subscription pricing saved!`);
      setSubscriptionForm({
        platform: "onlyfans_vip",
        duration_months: 1,
        regular_price: "",
        promo_price: "",
        promo_description: "",
        promo_active: false,
        promo_start_date: "",
        promo_end_date: "",
      });
      await loadData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save subscription pricing");
    } finally {
      setSavingSubscription(false);
    }
  };

  const filteredVideos = videos.filter(v => {
    // Show all videos, but we'll highlight the current platform column
    return true;
  });

  const platform = PLATFORMS.find(p => p.key === platformTab);

  return (
    <AppLayout
      title="Pricing Strategy & Recommendations"
      overline="AI-POWERED · MULTI-PLATFORM · PRICING OPTIMIZATION"
      subtitle={`${videos.length} videos · AI-powered recommendations with xAI Grok`}
      actions={
        <div className="flex gap-2">
          <button
            onClick={loadData}
            disabled={loading}
            className="btn-ghost inline-flex items-center gap-2 text-xs">
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
            Refresh
          </button>
          <button
            onClick={getAIRecommendations}
            disabled={aiLoading}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg font-bold text-xs transition"
            style={{
              background: "linear-gradient(135deg, #f0f 0%, #a855f7 100%)",
              color: "#000"
            }}>
            <Sparkles size={14} className={aiLoading ? "animate-spin" : ""} />
            {aiLoading ? "Analyzing..." : "Get AI Recommendations"}
          </button>
        </div>
      }>
      
      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b pb-2" style={{ borderColor: "var(--gio-border)" }}>
        {[
          { key: "catalog", label: "Video Pricing", icon: Package },
          { key: "platform-management", label: "Platform Pricing", icon: Tag },
          { key: "subscriptions", label: "Subscriptions", icon: Crown },
          { key: "jff-performance", label: "JFF Performance", icon: BarChart3 },
          { key: "history", label: "Price History", icon: History },
          { key: "recommendations", label: "AI Insights", icon: Sparkles },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 rounded-lg inline-flex items-center gap-2 text-sm font-bold transition ${
              tab === t.key ? "" : "opacity-60 hover:opacity-100"
            }`}
            style={{
              background: tab === t.key ? "var(--gio-surface)" : "transparent",
              color: tab === t.key ? "var(--gio-text)" : "var(--gio-muted)",
              border: tab === t.key ? "1px solid var(--gio-border)" : "1px solid transparent",
            }}>
            <t.icon size={16} />
            {t.label}
          </button>
        ))}
      </div>

      {/* Catalog Tab */}
      {tab === "catalog" && (
        <div className="grid lg:grid-cols-[1fr_320px] gap-6">
          {/* Left: Catalog + Pricing Controls */}
          <div className="space-y-4">
            {/* Platform Tabs */}
            <div className="flex gap-2">
              {PLATFORMS.map((p) => (
                <button
                  key={p.key}
                  onClick={() => setPlatformTab(p.key)}
                  className={`px-4 py-2 rounded-lg font-bold text-sm transition ${
                    platformTab === p.key ? "" : "opacity-60 hover:opacity-100"
                  }`}
                  style={{
                    background: platformTab === p.key ? `${p.color}22` : "transparent",
                    color: platformTab === p.key ? p.color : "var(--gio-muted)",
                    border: `1px solid ${platformTab === p.key ? p.color : "transparent"}`,
                  }}>
                  {p.label}
                </button>
              ))}
            </div>

            {/* Bulk Actions Bar */}
            {selectedVideos.size > 0 && (
              <div
                className="flex items-center gap-3 p-4 rounded-lg border"
                style={{
                  borderColor: "var(--gio-border)",
                  background: "var(--gio-surface)",
                }}>
                <div className="flex items-center gap-2">
                  <Check size={16} style={{ color: "var(--gio-primary)" }} />
                  <span className="font-bold text-sm" style={{ color: "var(--gio-text)" }}>
                    {selectedVideos.size} selected
                  </span>
                </div>
                <input
                  type="number"
                  step="0.01"
                  value={bulkPrice}
                  onChange={(e) => setBulkPrice(e.target.value)}
                  placeholder="Bulk price"
                  className="gio-input w-24"
                />
                <button
                  onClick={applyBulkPrice}
                  className="btn-ghost text-xs">
                  Apply to Selected
                </button>
                <input
                  type="text"
                  value={promoTag}
                  onChange={(e) => setPromoTag(e.target.value)}
                  placeholder="Promo tag (optional)"
                  className="gio-input flex-1"
                />
                <button
                  onClick={savePrices}
                  disabled={savingPrices}
                  className="btn-primary inline-flex items-center gap-2 text-xs">
                  <Save size={14} />
                  {savingPrices ? "Saving..." : "Save Prices"}
                </button>
              </div>
            )}

            {/* Videos Table */}
            <div
              className="rounded-lg border overflow-hidden"
              style={{ borderColor: "var(--gio-border)" }}>
              <table className="w-full text-sm">
                <thead>
                  <tr
                    className="border-b text-left"
                    style={{
                      borderColor: "var(--gio-border)",
                      background: "var(--gio-surface)",
                    }}>
                    <th className="p-3">
                      <input
                        type="checkbox"
                        checked={selectedVideos.size === videos.length && videos.length > 0}
                        onChange={selectAll}
                      />
                    </th>
                    <th className="p-3 font-mono-ui text-xs uppercase tracking-wider" style={{ color: "var(--gio-muted-3)" }}>
                      Video
                    </th>
                    <th className="p-3 font-mono-ui text-xs uppercase tracking-wider text-right" style={{ color: "var(--gio-muted-3)" }}>
                      {platform?.label}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {loading && (
                    <tr>
                      <td colSpan="3" className="p-8 text-center" style={{ color: "var(--gio-muted-3)" }}>
                        Loading catalog...
                      </td>
                    </tr>
                  )}
                  {!loading && filteredVideos.length === 0 && (
                    <tr>
                      <td colSpan="3" className="p-12 text-center" style={{ color: "var(--gio-muted-3)" }}>
                        No videos in catalog
                      </td>
                    </tr>
                  )}
                  {filteredVideos.map((video) => {
                    const isSelected = selectedVideos.has(video.id);
                    const currentPrice = video.pricing?.[platform?.field];
                    
                    return (
                      <tr
                        key={video.id}
                        className="border-b transition hover:bg-[color:var(--gio-primary-soft)]"
                        style={{ borderColor: "var(--gio-border)" }}>
                        <td className="p-3">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleVideoSelection(video.id)}
                          />
                        </td>
                        <td className="p-3">
                          <div className="flex items-center gap-3">
                            {video.thumbnail && (
                              <img
                                src={video.thumbnail}
                                alt={video.title}
                                className="w-16 h-12 object-cover rounded"
                              />
                            )}
                            <div className="min-w-0 flex-1">
                              <div className="font-bold text-sm truncate" style={{ color: "var(--gio-text)" }}>
                                {video.title}
                              </div>
                              <div className="text-xs truncate" style={{ color: "var(--gio-muted-3)" }}>
                                {video.duration} · {video.tags?.slice(0, 3).join(", ")}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="p-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <span className="text-xs" style={{ color: "var(--gio-muted-3)" }}>$</span>
                            <input
                              type="number"
                              step="0.01"
                              value={currentPrice || ""}
                              onChange={(e) => handlePriceChange(video.id, platform.field, e.target.value)}
                              placeholder="0.00"
                              className="w-20 px-2 py-1 rounded border text-right"
                              style={{
                                borderColor: "var(--gio-border)",
                                background: "var(--gio-surface)",
                                color: "var(--gio-text)",
                              }}
                            />
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Right: Performance Metrics */}
          <div className="space-y-4">
            <div
              className="rounded-lg border p-5"
              style={{
                borderColor: "var(--gio-border)",
                background: "var(--gio-surface)",
              }}>
              <h3 className="font-bold text-sm mb-4 flex items-center gap-2">
                <BarChart3 size={16} style={{ color: "var(--gio-primary)" }} />
                Price Tier Performance
              </h3>
              {metrics && Object.entries(metrics).map(([tier, data]) => (
                <div key={tier} className="mb-3 pb-3 border-b last:border-0" style={{ borderColor: "var(--gio-border)" }}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-bold text-xs" style={{ color: "var(--gio-text)" }}>{tier}</span>
                    <span className="text-xs" style={{ color: "var(--gio-muted-2)" }}>
                      {data.video_count} videos
                    </span>
                  </div>
                  <div className="text-xs" style={{ color: "var(--gio-muted-3)" }}>
                    Avg: ${data.avg_price?.toFixed(2) || "0.00"}
                  </div>
                </div>
              ))}
            </div>

            <div
              className="rounded-lg border p-5"
              style={{
                borderColor: "#f0f",
                background: "linear-gradient(135deg, #f0f11 0%, #a855f722 100%)",
              }}>
              <div className="flex items-center gap-2 mb-3">
                <Zap size={16} style={{ color: "#f0f" }} />
                <h3 className="font-bold text-sm" style={{ color: "#f0f" }}>
                  AI-Powered Insights
                </h3>
              </div>
              <p className="text-xs mb-4" style={{ color: "var(--gio-muted-2)" }}>
                Get data-driven pricing recommendations based on your sales history, rebill rates, and platform performance.
              </p>
              <button
                onClick={getAIRecommendations}
                disabled={aiLoading}
                className="w-full py-2 rounded-lg font-bold text-xs transition"
                style={{
                  background: "linear-gradient(135deg, #f0f 0%, #a855f7 100%)",
                  color: "#000",
                }}>
                {aiLoading ? "Analyzing..." : "Generate Recommendations"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Subscriptions Tab */}
      {tab === "subscriptions" && (
        <div className="grid lg:grid-cols-[1fr_400px] gap-6">
          {/* Left: Add/Edit Subscription Pricing */}
          <div className="space-y-4">
            <div
              className="rounded-lg border p-6"
              style={{
                borderColor: "var(--gio-border)",
                background: "var(--gio-surface)",
              }}>
              <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                <Crown size={20} style={{ color: "var(--gio-primary)" }} />
                Subscription Tier Pricing
              </h3>
              
              <div className="grid md:grid-cols-2 gap-4 mb-4">
                {/* Platform Selection */}
                <div>
                  <label className="block text-sm font-bold mb-2" style={{ color: "var(--gio-text)" }}>
                    Platform
                  </label>
                  <select
                    value={subscriptionForm.platform}
                    onChange={(e) => setSubscriptionForm({...subscriptionForm, platform: e.target.value})}
                    className="gio-input w-full">
                    <option value="onlyfans_vip">OnlyFans VIP</option>
                    <option value="onlyfans_free">OnlyFans Free</option>
                    <option value="justforfans">JustForFans</option>
                  </select>
                </div>

                {/* Duration */}
                <div>
                  <label className="block text-sm font-bold mb-2" style={{ color: "var(--gio-text)" }}>
                    Duration
                  </label>
                  <select
                    value={subscriptionForm.duration_months}
                    onChange={(e) => setSubscriptionForm({...subscriptionForm, duration_months: e.target.value})}
                    className="gio-input w-full">
                    <option value="1">1 Month</option>
                    <option value="3">3 Months</option>
                    <option value="6">6 Months</option>
                    <option value="12">12 Months</option>
                  </select>
                </div>

                {/* Regular Price */}
                <div>
                  <label className="block text-sm font-bold mb-2" style={{ color: "var(--gio-text)" }}>
                    Regular Price ($) *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={subscriptionForm.regular_price}
                    onChange={(e) => setSubscriptionForm({...subscriptionForm, regular_price: e.target.value})}
                    placeholder="0.00"
                    className="gio-input w-full"
                    required
                  />
                </div>

                {/* Promo Price */}
                <div>
                  <label className="block text-sm font-bold mb-2" style={{ color: "var(--gio-text)" }}>
                    Promo Price ($)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={subscriptionForm.promo_price}
                    onChange={(e) => setSubscriptionForm({...subscriptionForm, promo_price: e.target.value})}
                    placeholder="Optional"
                    className="gio-input w-full"
                  />
                </div>
              </div>

              {/* Promo Description */}
              <div className="mb-4">
                <label className="block text-sm font-bold mb-2" style={{ color: "var(--gio-text)" }}>
                  Promo Description
                </label>
                <input
                  type="text"
                  value={subscriptionForm.promo_description}
                  onChange={(e) => setSubscriptionForm({...subscriptionForm, promo_description: e.target.value})}
                  placeholder="e.g., New Year Sale - 20% OFF"
                  className="gio-input w-full"
                />
              </div>

              {/* Promo Active Toggle */}
              <div className="mb-4 flex items-center gap-3">
                <input
                  type="checkbox"
                  id="promo-active"
                  checked={subscriptionForm.promo_active}
                  onChange={(e) => setSubscriptionForm({...subscriptionForm, promo_active: e.target.checked})}
                  className="w-4 h-4"
                />
                <label htmlFor="promo-active" className="text-sm font-bold" style={{ color: "var(--gio-text)" }}>
                  Promo Active
                </label>
              </div>

              {/* Promo Dates (conditional) */}
              {subscriptionForm.promo_active && (
                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-bold mb-2" style={{ color: "var(--gio-text)" }}>
                      Promo Start Date
                    </label>
                    <input
                      type="date"
                      value={subscriptionForm.promo_start_date}
                      onChange={(e) => setSubscriptionForm({...subscriptionForm, promo_start_date: e.target.value})}
                      className="gio-input w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold mb-2" style={{ color: "var(--gio-text)" }}>
                      Promo End Date
                    </label>
                    <input
                      type="date"
                      value={subscriptionForm.promo_end_date}
                      onChange={(e) => setSubscriptionForm({...subscriptionForm, promo_end_date: e.target.value})}
                      className="gio-input w-full"
                    />
                  </div>
                </div>
              )}

              <button
                onClick={saveSubscriptionPricing}
                disabled={savingSubscription}
                className="btn-primary inline-flex items-center gap-2">
                <Save size={16} />
                {savingSubscription ? "Saving..." : "Save Subscription Pricing"}
              </button>
            </div>
          </div>

          {/* Right: Existing Subscription Pricing */}
          <div className="space-y-4">
            <div
              className="rounded-lg border p-5"
              style={{
                borderColor: "var(--gio-border)",
                background: "var(--gio-surface)",
              }}>
              <h3 className="font-bold text-sm mb-4 flex items-center gap-2">
                <Crown size={16} style={{ color: "var(--gio-primary)" }} />
                Current Subscription Plans
              </h3>
              
              {Object.keys(subscriptionPricing).length === 0 && (
                <p className="text-sm text-center py-8" style={{ color: "var(--gio-muted-3)" }}>
                  No subscription pricing configured yet
                </p>
              )}

              {Object.entries(subscriptionPricing).map(([platformKey, plans]) => {
                const platformNames = {
                  onlyfans_vip: "OnlyFans VIP",
                  onlyfans_free: "OnlyFans Free",
                  justforfans: "JustForFans"
                };
                return (
                  <div key={platformKey} className="mb-6 pb-4 border-b last:border-0" style={{ borderColor: "var(--gio-border)" }}>
                    <div className="font-bold text-xs uppercase mb-3" style={{ color: "var(--gio-primary)" }}>
                      {platformNames[platformKey] || platformKey}
                    </div>
                    {plans.map((plan, i) => (
                      <div key={i} className="mb-3 pb-3 border-b last:border-0" style={{ borderColor: "var(--gio-border)" }}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-bold text-sm" style={{ color: "var(--gio-text)" }}>
                            {plan.duration_months} {plan.duration_months === 1 ? "Month" : "Months"}
                          </span>
                          <span className="font-bold" style={{ color: "var(--gio-secondary)" }}>
                            ${Number(plan.regular_price).toFixed(2)}
                          </span>
                        </div>
                        {plan.promo_active && plan.promo_price && (
                          <div className="flex items-center gap-2 mt-1">
                            <span
                              className="px-2 py-0.5 rounded text-xs font-bold"
                              style={{
                                background: "var(--gio-primary-soft)",
                                color: "var(--gio-primary)",
                              }}>
                              PROMO: ${Number(plan.promo_price).toFixed(2)}
                            </span>
                            {plan.promo_description && (
                              <span className="text-xs" style={{ color: "var(--gio-muted-2)" }}>
                                {plan.promo_description}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {tab === "platform-management" && <PricingPlatformManagement />}

      {tab === "jff-performance" && <JffReportedPerformance data={jffReportedPerformance} />}

      {/* History Tab */}
      {tab === "history" && (
        <div className="space-y-4">
          <div
            className="rounded-lg border overflow-hidden"
            style={{ borderColor: "var(--gio-border)" }}>
            <table className="w-full text-sm">
              <thead>
                <tr
                  className="border-b text-left"
                  style={{
                    borderColor: "var(--gio-border)",
                    background: "var(--gio-surface)",
                  }}>
                  <th className="p-3 font-mono-ui text-xs uppercase" style={{ color: "var(--gio-muted-3)" }}>Date</th>
                  <th className="p-3 font-mono-ui text-xs uppercase" style={{ color: "var(--gio-muted-3)" }}>Video</th>
                  <th className="p-3 font-mono-ui text-xs uppercase" style={{ color: "var(--gio-muted-3)" }}>Platform</th>
                  <th className="p-3 font-mono-ui text-xs uppercase text-right" style={{ color: "var(--gio-muted-3)" }}>Price</th>
                  <th className="p-3 font-mono-ui text-xs uppercase" style={{ color: "var(--gio-muted-3)" }}>Tag</th>
                </tr>
              </thead>
              <tbody>
                {history.length === 0 && (
                  <tr>
                    <td colSpan="5" className="p-12 text-center" style={{ color: "var(--gio-muted-3)" }}>
                      No pricing history yet
                    </td>
                  </tr>
                )}
                {history.map((entry, i) => (
                  <tr
                    key={i}
                    className="border-b hover:bg-[color:var(--gio-primary-soft)]"
                    style={{ borderColor: "var(--gio-border)" }}>
                    <td className="p-3 text-xs" style={{ color: "var(--gio-muted-2)" }}>
                      {new Date(entry.change_date).toLocaleDateString()}
                    </td>
                    <td className="p-3 text-xs" style={{ color: "var(--gio-text)" }}>
                      {videos.find(v => v.id === entry.video_id)?.title || entry.video_id}
                    </td>
                    <td className="p-3">
                      <span
                        className="px-2 py-0.5 rounded text-xs font-bold"
                        style={{
                          background: PLATFORMS.find(p => entry.platform.includes(p.key))?.color + "22" || "var(--gio-surface)",
                          color: PLATFORMS.find(p => entry.platform.includes(p.key))?.color || "var(--gio-muted)",
                        }}>
                        {entry.platform.replace("_", " ")}
                      </span>
                    </td>
                    <td className="p-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {entry.previous_price && (
                          <span className="text-xs line-through" style={{ color: "var(--gio-muted-3)" }}>
                            ${entry.previous_price.toFixed(2)}
                          </span>
                        )}
                        <span className="font-bold" style={{ color: "var(--gio-secondary)" }}>
                          ${entry.price.toFixed(2)}
                        </span>
                      </div>
                    </td>
                    <td className="p-3">
                      {entry.promo_tag && (
                        <span
                          className="px-2 py-0.5 rounded text-xs"
                          style={{
                            background: "var(--gio-primary-soft)",
                            color: "var(--gio-primary)",
                          }}>
                          {entry.promo_tag}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* AI Recommendations Tab */}
      {tab === "recommendations" && (
        <div className="space-y-4">
          {!recommendations && (
            <div className="text-center py-12">
              <Sparkles size={48} className="mx-auto mb-4" style={{ color: "#f0f" }} />
              <p className="text-lg font-bold mb-2" style={{ color: "var(--gio-text)" }}>
                No recommendations yet
              </p>
              <p className="text-sm mb-6" style={{ color: "var(--gio-muted-2)" }}>
                Click "Get AI Recommendations" to generate pricing insights
              </p>
              <button
                onClick={getAIRecommendations}
                disabled={aiLoading}
                className="px-6 py-3 rounded-lg font-bold text-sm"
                style={{
                  background: "linear-gradient(135deg, #f0f 0%, #a855f7 100%)",
                  color: "#000",
                }}>
                {aiLoading ? "Analyzing..." : "Generate Recommendations"}
              </button>
            </div>
          )}

          {recommendations && (
            <>
              {/* Summary Card */}
              {recommendations.summary && (
                <div
                  className="rounded-lg border p-6"
                  style={{
                    borderColor: "#f0f",
                    background: "linear-gradient(135deg, #f0f11 0%, #a855f722 100%)",
                  }}>
                  <div className="flex items-start gap-3">
                    <Target size={24} style={{ color: "#f0f" }} />
                    <div>
                      <h3 className="font-bold text-lg mb-2" style={{ color: "#f0f" }}>
                        Strategy Summary
                      </h3>
                      <p className="text-sm" style={{ color: "var(--gio-text)" }}>
                        {recommendations.summary}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Recommendations List */}
              {recommendations.recommendations?.map((rec, i) => (
                <div
                  key={i}
                  className="rounded-lg border p-5"
                  style={{
                    borderColor: "var(--gio-border)",
                    background: "var(--gio-surface)",
                  }}>
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span
                          className="px-2 py-0.5 rounded text-xs font-bold"
                          style={{
                            background: PLATFORMS.find(p => rec.platform.includes(p.key))?.color + "22" || "#f0f22",
                            color: PLATFORMS.find(p => rec.platform.includes(p.key))?.color || "#f0f",
                          }}>
                          {PLATFORMS.find(p => rec.platform.includes(p.key))?.label || rec.platform}
                        </span>
                        {rec.priority === "high" && (
                          <span
                            className="px-2 py-0.5 rounded text-xs font-bold"
                            style={{
                              background: "#ef444422",
                              color: "#ef4444",
                            }}>
                            HIGH PRIORITY
                          </span>
                        )}
                      </div>
                      <div className="text-sm flex items-center gap-3 mt-2">
                        <span style={{ color: "var(--gio-muted-2)" }}>
                          Current: <span className="font-mono">{rec.current_range}</span>
                        </span>
                        <span style={{ color: "var(--gio-muted-3)" }}>→</span>
                        <span style={{ color: "var(--gio-secondary)" }}>
                          Recommended: <span className="font-mono font-bold">{rec.recommended_range}</span>
                        </span>
                      </div>
                    </div>
                  </div>
                  <p className="text-sm mb-4" style={{ color: "var(--gio-text)" }}>
                    {rec.reasoning}
                  </p>
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </AppLayout>
  );
}

function JffReportedPerformance({ data }) {
  const formatMoney = (value, currency) => {
    if (value === null || value === undefined) return "Not visible in captured table";
    if (!currency) return `${Number(value).toLocaleString()} · currency unavailable`;
    return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(Number(value));
  };
  const observedAt = (value) => value ? new Date(value).toLocaleString() : "Capture time unavailable";
  const promoCodes = data.promo_codes || [];
  const reasons = data.cancellation_reasons || [];

  return (
    <section className="space-y-4" data-testid="jff-reported-performance">
      <div className="border-y py-3 text-xs" style={{ borderColor: "var(--gio-border)", color: "var(--gio-muted-2)" }}>
        JFF-reported observations · visible rows only · not Finance ledger revenue or configured prices
      </div>
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="border-t pt-3" style={{ borderColor: "var(--gio-border)" }}>
          <h3 className="mb-2 text-sm font-semibold" style={{ color: "var(--gio-text)" }}>Promo code usage</h3>
          {promoCodes.length === 0 ? <p className="py-5 text-xs" style={{ color: "var(--gio-muted-3)" }}>No captured promo-code rows.</p> : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead><tr className="border-b" style={{ borderColor: "var(--gio-border)", color: "var(--gio-muted-3)" }}>
                  <th className="py-2 pr-3">Code</th><th className="py-2 pr-3">Price</th><th className="py-2 pr-3">Uses</th><th className="py-2">Reported total</th>
                </tr></thead>
                <tbody>{promoCodes.map((promo, index) => <tr key={`${promo.account_id}:${promo.code}:${index}`} className="border-b" style={{ borderColor: "var(--gio-border)" }}>
                  <td className="py-2 pr-3" style={{ color: "var(--gio-text)" }}>{promo.code || "Unknown code"}<div className="text-[10px]" style={{ color: "var(--gio-muted-3)" }}>{promo.account_id} · {observedAt(promo.observed_at)}</div></td>
                  <td className="py-2 pr-3" style={{ color: "var(--gio-muted-2)" }}>{formatMoney(promo.price, promo.currency)}</td>
                  <td className="py-2 pr-3" style={{ color: "var(--gio-muted-2)" }}>{promo.uses ?? "Not visible"}</td>
                  <td className="py-2" style={{ color: "var(--gio-muted-2)" }}>{formatMoney(promo.reported_total, promo.currency)}</td>
                </tr>)}</tbody>
              </table>
            </div>
          )}
        </div>
        <div className="border-t pt-3" style={{ borderColor: "var(--gio-border)" }}>
          <h3 className="mb-2 text-sm font-semibold" style={{ color: "var(--gio-text)" }}>Cancellation reasons</h3>
          {reasons.length === 0 ? <p className="py-5 text-xs" style={{ color: "var(--gio-muted-3)" }}>No captured cancellation rows.</p> : (
            <div className="space-y-2">
              {reasons.map((reason, index) => <div key={`${reason.account_id}:${reason.category}:${index}`} className="flex items-center justify-between gap-3 border-b py-2" style={{ borderColor: "var(--gio-border)" }}>
                <div className="min-w-0"><div className="truncate text-xs" style={{ color: "var(--gio-text)" }}>{reason.category}</div><div className="text-[10px]" style={{ color: "var(--gio-muted-3)" }}>{reason.account_id} · {observedAt(reason.observed_at)}</div></div>
                <div className="shrink-0 text-right text-xs" style={{ color: "var(--gio-muted-2)" }}>{reason.count ?? "Not visible"} · {formatMoney(reason.reported_lost_amount, reason.currency)}</div>
              </div>)}
            </div>
          )}
          <p className="mt-3 text-[10px]" style={{ color: "var(--gio-muted-3)" }}>Freeform cancellation text is not retained. Hidden Lost $ values remain unavailable.</p>
        </div>
      </div>
    </section>
  );
}
